<template>
  <div v-if="isBannerShown" class="relative max-h-[90px] min-h-[90px]">
    <img
      :src="bannerSrc"
      alt="Freedom Travel распродажа"
      class="max-h-[90px] w-full rounded-2xl"
    />
    <div
      class="absolute right-10 top-3.5 z-10 flex flex-col items-center justify-center space-y-1"
    >
      <div
        v-if="saleDiscount?.badge_title"
        class="sale-discount-background flex h-[26px] min-w-fit items-center rounded-xl px-1.5"
      >
        <span
          class="sale-discount-text font-geologica block min-w-fit text-xl font-black italic leading-none"
        >
          {{ saleDiscount.badge_title }}
        </span>
      </div>
      <div v-if="allAirlineIatas.length" class="flex -space-x-2">
        <img
          v-for="(iata, index) in allAirlineIatas"
          :key="iata"
          :src="airlineLogoUrl(iata)"
          :alt="iata"
          class="shrink-0 object-contain"
          :style="{ zIndex: index }"
          width="37"
          height="37"
        />
      </div>
    </div>
    <div
      class="bg-background-negative-primary text-static-white text-2xs absolute -top-2.5 right-6 z-10 rounded-xl px-1.5 py-1 font-semibold"
    >
      {{ quotaLabelText }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { type flightEntity } from 'freedom-shared';
  import type { Airflow } from '@aviata/platform-types';

  import { analyticsEventsLib } from '@/shared/lib';
  import { staticUrls } from '@/shared/config';

  import saleBannerEn from '../assets/sale_banner_en.webp';
  import saleBannerKk from '../assets/sale_banner_kk.webp';
  import saleBannerRu from '../assets/sale_banner_ru.webp';

  const AIRLINE_LOGO_BASE = staticUrls.jasylJumaBannersBase;

  type DiscountWithQuota = Airflow.Offer.Discount & {
    quota?: {
      available?: number;
      airlines?: string[];
    };
  };

  type SaleBannerLocale = 'ru' | 'kk' | 'en';

  const BANNER_BY_LOCALE: Record<SaleBannerLocale, string> = {
    ru: saleBannerRu,
    kk: saleBannerKk,
    en: saleBannerEn,
  };

  const props = defineProps<{
    activeOffer?: flightEntity.types.MappedOffer;
  }>();

  const { locale, t } = useI18n();

  const bannerSrc = computed(() => {
    const loc = locale.value as SaleBannerLocale;
    return BANNER_BY_LOCALE[loc] ?? BANNER_BY_LOCALE.ru;
  });

  const allAirlineIatas = computed(() => {
    const discount = props.activeOffer?.features?.discount as
      | DiscountWithQuota
      | undefined;
    return discount?.quota?.airlines || [];
  });

  const airlineLogoUrl = (iata: string) => `${AIRLINE_LOGO_BASE}/${iata}.webp`;

  const saleDiscount = computed(() => {
    const discount = props.activeOffer?.features?.discount as
      | DiscountWithQuota
      | undefined;
    if (!discount) return undefined;

    const value = discount.quota?.available;
    const available =
      typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

    return {
      available,
      badge_title: discount.badge_title,
    };
  });

  const isBannerShown = computed(
    () =>
      saleDiscount.value?.available != null && saleDiscount.value.available > 1,
  );

  const quotaLabelText = computed(() =>
    t('details.saleBanner.quotaLabel', saleDiscount.value?.available ?? 0),
  );

  onMounted(() => {
    if (isBannerShown.value) {
      analyticsEventsLib.jjDetailsBannerShow({
        sale: saleDiscount.value?.badge_title ?? '',
        airlines: allAirlineIatas.value,
        quota: saleDiscount.value?.available ?? 0,
      });
    }
  });
</script>

<style scoped>
  .sale-discount-background {
    background: linear-gradient(
      90.08deg,
      #17e860 11.05%,
      #69fa9b 49.46%,
      #17e860 88.61%
    );
  }

  .sale-discount-text {
    background: linear-gradient(
      95.54deg,
      #272727 22.19%,
      #717171 57.98%,
      #272727 81.74%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
</style>
